/**
 * Observability Types
 *
 * Defines the interfaces for telemetry and monitoring integration.
 * This allows the shared package to remain framework-agnostic while
 * enabling integration with third-party services like Sentry, Datadog, etc.
 *
 * @module types/observability
 */

// =============================================================================
// SEVERITY LEVELS
// =============================================================================

/**
 * Severity levels for observability events
 *
 * These levels help categorize and filter events based on their importance.
 *
 * @example
 * ```typescript
 * import { ObservabilitySeverity } from '../types/observability';
 *
 * const level = ObservabilitySeverity.ERROR;
 * console.log(level); // 'error'
 * ```
 */
export enum ObservabilitySeverity {
  /** Debug-level messages for development */
  DEBUG = 'debug',
  /** Informational messages */
  INFO = 'info',
  /** Warning messages that don't require immediate action */
  WARNING = 'warning',
  /** Error messages that require attention */
  ERROR = 'error',
  /** Fatal errors that cause system failure */
  FATAL = 'fatal',
}

// =============================================================================
// METRIC UNITS
// =============================================================================

/**
 * Valid unit types for observability metrics
 */
export type MetricUnit = 'milliseconds' | 'bytes' | 'count' | 'percentage';

// =============================================================================
// EVENT TYPES
// =============================================================================

/**
 * Observability event for non-error messages
 *
 * @example
 * ```typescript
 * import { ObservabilityEvent, ObservabilitySeverity } from '../types/observability';
 *
 * const event: ObservabilityEvent = {
 *   message: 'User logged in successfully',
 *   severity: ObservabilitySeverity.INFO,
 *   timestamp: dayjs().valueOf(),
 *   context: { userId: '123' },
 *   tags: { feature: 'auth' },
 * };
 * ```
 */
export interface ObservabilityEvent {
  /** Human-readable message describing the event */
  message: string;
  /** Severity level of the event */
  severity: ObservabilitySeverity;
  /** Unix timestamp (milliseconds) when the event occurred */
  timestamp: number;
  /** Additional context data for the event */
  context?: Record<string, unknown>;
  /** Tags for filtering and grouping events */
  tags?: Record<string, string>;
}

/**
 * Observability error event
 *
 * @example
 * ```typescript
 * import { ObservabilityError, ObservabilitySeverity } from '../types/observability';
 *
 * const errorEvent: ObservabilityError = {
 *   error: new Error('Network request failed'),
 *   severity: ObservabilitySeverity.ERROR,
 *   context: { endpoint: '/api/users' },
 *   tags: { category: 'network' },
 * };
 * ```
 */
export interface ObservabilityError {
  /** The error object to capture */
  error: Error;
  /** Severity level of the error */
  severity: ObservabilitySeverity;
  /** Additional context data for the error */
  context?: Record<string, unknown>;
  /** Tags for filtering and grouping errors */
  tags?: Record<string, string>;
}

/**
 * Observability metric for tracking measurements
 *
 * @example
 * ```typescript
 * import { ObservabilityMetric } from '../types/observability';
 *
 * const metric: ObservabilityMetric = {
 *   name: 'api_response_time',
 *   value: 250,
 *   unit: 'milliseconds',
 *   tags: { endpoint: '/api/users' },
 * };
 * ```
 */
export interface ObservabilityMetric {
  /** Name of the metric */
  name: string;
  /** Numeric value of the metric */
  value: number;
  /** Unit of measurement */
  unit?: MetricUnit;
  /** Tags for filtering and grouping metrics */
  tags?: Record<string, string>;
}

/**
 * User information for observability context
 *
 * @example
 * ```typescript
 * import { ObservabilityUser } from '../types/observability';
 *
 * const user: ObservabilityUser = {
 *   id: '123',
 *   email: 'user@example.com',
 *   username: 'johndoe',
 *   plan: 'premium',
 * };
 * ```
 */
export interface ObservabilityUser {
  /** Unique identifier for the user */
  id: string;
  /** User's email address */
  email?: string;
  /** User's username */
  username?: string;
  /** Additional user properties */
  [key: string]: unknown;
}

/**
 * Breadcrumb for tracking the sequence of events
 *
 * @example
 * ```typescript
 * import { ObservabilityBreadcrumb, ObservabilitySeverity } from '../types/observability';
 *
 * const breadcrumb: ObservabilityBreadcrumb = {
 *   message: 'User clicked checkout button',
 *   category: 'ui.click',
 *   level: ObservabilitySeverity.INFO,
 *   timestamp: dayjs().valueOf(),
 *   data: { buttonId: 'checkout-btn' },
 * };
 * ```
 */
export interface ObservabilityBreadcrumb {
  /** Human-readable message describing the breadcrumb */
  message: string;
  /** Category for grouping breadcrumbs */
  category?: string;
  /** Severity level of the breadcrumb */
  level?: ObservabilitySeverity;
  /** Unix timestamp (milliseconds) when the breadcrumb was created */
  timestamp?: number;
  /** Additional data for the breadcrumb */
  data?: Record<string, unknown>;
}

// =============================================================================
// OBSERVABILITY PROVIDER INTERFACE
// =============================================================================

/**
 * ObservabilityProvider interface
 *
 * This interface defines the contract for telemetry/monitoring providers.
 * Implementations can integrate with services like Sentry, Datadog, LogRocket, etc.
 *
 * The shared package uses this interface to report events without knowing
 * the specific implementation details.
 *
 * @example
 * ```typescript
 * import { ObservabilityProvider, ObservabilityEvent } from '../types/observability';
 *
 * class SentryProvider implements ObservabilityProvider {
 *   captureMessage(event: ObservabilityEvent): void {
 *     Sentry.captureMessage(event.message, {
 *       level: event.severity,
 *       extra: event.context,
 *       tags: event.tags,
 *     });
 *   }
 *   // ... other methods
 * }
 * ```
 */
export interface ObservabilityProvider {
  /**
   * Captures a message event (non-error event)
   * Use for informational messages, warnings, or debug logs
   */
  captureMessage(event: ObservabilityEvent): void;

  /**
   * Captures an error/exception
   * Use for errors and exceptions that need tracking
   */
  captureError(error: ObservabilityError): void;

  /**
   * Records a custom metric
   * Use for performance metrics, counts, or measurements
   */
  recordMetric(metric: ObservabilityMetric): void;

  /**
   * Sets the current user context
   * User information will be attached to all subsequent events
   */
  setUser(user: ObservabilityUser | null): void;

  /**
   * Adds a breadcrumb to the trail
   * Breadcrumbs help reconstruct the sequence of events leading to an error
   */
  addBreadcrumb(breadcrumb: ObservabilityBreadcrumb): void;

  /**
   * Sets a custom tag/label
   * Tags help filter and group events
   */
  setTag(key: string, value: string): void;

  /**
   * Sets custom context data
   * Context provides additional information about the environment
   */
  setContext(name: string, context: Record<string, unknown>): void;

  /**
   * Clears all user and context data
   * Use when user logs out or session ends
   */
  clearContext(): void;
}

// =============================================================================
// OBSERVABILITY CONFIGURATION
// =============================================================================

/**
 * Configuration options for the observability system
 *
 * @example
 * ```typescript
 * import { ObservabilityConfig, ObservabilitySeverity } from '../types/observability';
 *
 * const config: ObservabilityConfig = {
 *   enabled: true,
 *   defaultTags: { app: 'my-app', version: '1.0.0' },
 *   beforeCapture: (event) => event.severity !== ObservabilitySeverity.DEBUG,
 * };
 * ```
 */
export interface ObservabilityConfig {
  /** Whether observability is enabled */
  enabled: boolean;
  /** The provider implementation to use */
  provider?: ObservabilityProvider;
  /** Default tags applied to all events */
  defaultTags?: Record<string, string>;
  /** Hook to filter events before capture (return false to skip capture) */
  beforeCapture?: (event: ObservabilityEvent | ObservabilityError) => boolean;
  /**
   * Whether failed requests answered with a 4xx are reported (at WARNING).
   * On during the beta to surface contract drift, permission bugs and broken
   * session flows; network failures and 5xx are always reported.
   */
  reportClientErrors?: boolean;
}

/**
 * Default configuration for observability
 */
export const DEFAULT_OBSERVABILITY_CONFIG: ObservabilityConfig = {
  enabled: false,
  reportClientErrors: true,
};
